import { aiPriceGap } from './aiPrice';

// Anomaly signals: the verdicts the backend assigns per unit, how they group
// on the inventory page, and the rule text behind each one. Shared by the
// current AnomalyStrip and the redesigned (v2) inventory page.

// ─── Verdict definitions ────────────────────────────────────────────────────

export const VERDICT_CONFIG = {
    'Model Underperformer': {
        severity: 'critical',
        tooltip: [
            'Quote Rate < 30% of same make/model/year peers',
            'Compares this unit\'s quote rate (quotes ÷ days in stock) against the median for all other in-stock units with the same Make / Model / Year.',
            'Fires when this unit gets less than 30% of the quotes its peers receive. The category is healthy — this specific unit is the problem.',
            'Requires ≥ 2 peers of the same model + year.',
        ],
    },
    'Unit Anomaly': {
        severity: 'critical',
        tooltip: [
            'Quote Rate < 30% of category — no comparable units',
            'Falls back to a category-wide comparison (e.g. all Combines) when fewer than 2 same-model peers are in stock.',
            'Fires when this unit gets less than 30% of the category median quote rate AND the category itself is healthy.',
            'If same-model peers exist, "Model Underperformer" fires instead.',
        ],
    },
    'Category Slowdown': {
        severity: 'warning',
        tooltip: [
            'Whole segment is below market norm',
            'Two conditions must both be true: (1) this unit\'s quote rate is below 35% of its category median, AND (2) the category\'s median quote rate is below 70% of the market-wide median across all categories.',
            'The 70% threshold (vs the stricter 50% used elsewhere) gives cheaper equipment categories more room — they naturally attract fewer quotes.',
            'Fleet-level signal: the issue is not this unit, the whole category is slow. Response: category-wide pricing or financing programs.',
        ],
    },
    'Overpriced vs Peers': {
        severity: 'warning',
        tooltip: [
            'Asking price > 15% above peer median',
            'Compares this unit\'s asking price against the median asking price of all in-stock units with the same Make / Model / Year.',
            'Fires when asking price exceeds the peer median by more than 15%. May explain lower quote activity.',
            'Requires ≥ 2 peers of the same model+year.',
        ],
    },
    'Aged vs Peers': {
        severity: 'warning',
        tooltip: [
            'Days in stock > 50% above peer median',
            'Compares how long this unit has been in stock against the median days-in-stock for all in-stock units with the same Make / Model / Year.',
            'Fires when this unit has been sitting 50% longer than its peers are typically taking to sell.',
            'Requires ≥ 2 peers of the same model+year.',
        ],
    },
    'Above AI Estimate': {
        severity: 'warning',
        tooltip: [
            'Asking price > 5% above AI prediction',
            'Direct comparison between the current asking price and the AI\'s predicted sale price.',
            'Fires when asking price exceeds the AI estimate by more than 5%. Suggests the market may not support the current price — consider aligning closer to the AI estimate.',
        ],
    },
    'Outperformer': {
        severity: 'positive',
        tooltip: [
            'Quote Rate > 2× peer median',
            'This unit is receiving more than twice the quote activity of other in-stock units with the same Make / Model / Year.',
            'Strong demand signal — consider holding price or prioritising follow-up on active quotes.',
            'Requires ≥ 2 peers of the same model+year.',
        ],
    },
    'High Interest': {
        severity: 'positive',
        tooltip: [
            'Quote Rate > 2× category median',
            'Same as Outperformer but compared against the entire category median when fewer than 2 same-model peers exist.',
            'Unusually high demand relative to similar equipment types.',
        ],
    },
};

// The two price signals follow the dealership's thresholds (the backend
// raises them with the same settings): pricing.peer_overpriced_pct and
// pricing.gap_warn_pct, 15% and 5% by default.
const pctText = (n) => `${Number(Number(n).toFixed(1))}%`;

/**
 * A verdict's tooltip lines, with the dealership's thresholds in the two
 * price signals; null for an unknown verdict.
 */
export const verdictTooltip = (verdict, { peerOverpricedPct = 15, gapWarnPct = 5 } = {}) => {
    const cfg = VERDICT_CONFIG[verdict];
    if (!cfg) return null;
    if (verdict === 'Overpriced vs Peers') {
        const p = pctText(peerOverpricedPct);
        return [
            `Asking price > ${p} above peer median`,
            cfg.tooltip[1],
            `Fires when asking price exceeds the peer median by more than ${p}. May explain lower quote activity.`,
            cfg.tooltip[3],
        ];
    }
    if (verdict === 'Above AI Estimate') {
        const p = pctText(gapWarnPct);
        return [
            `Asking price > ${p} above AI prediction`,
            cfg.tooltip[1],
            `Fires when asking price exceeds the AI estimate by more than ${p}. Suggests the market may not support the current price — consider aligning closer to the AI estimate.`,
        ];
    }
    return cfg.tooltip;
};

/**
 * A unit's anomaly reason as shown. For "Above AI Estimate" the percent in
 * the backend sentence is replaced by the gap as the chip shows it (one
 * decimal, e.g. "8.1%"), so a reason never reads "8% above" next to a
 * "+8.1%" chip and a threshold of "more than 8%". Other text is unchanged.
 */
export const anomalyReasonText = (unit) => {
    const reason = unit?.AnomalyReason || '';
    if (!reason || unit.AnomalyVerdict !== 'Above AI Estimate') return reason;
    const gap = aiPriceGap(unit);
    if (gap === null) return reason;
    const shown = Math.abs(gap * 100).toFixed(1);
    return reason.replace(/(\d+(?:[.,]\d+)?)\s?%\s+above/, `${shown}% above`);
};

export const ANOMALY_GROUPS = [
    {
        key:       'critical',
        label:     'Unit Issues',
        colorClass:'anomaly-critical',
        verdicts:  ['Model Underperformer', 'Unit Anomaly'],
    },
    {
        key:       'warning',
        label:     'Watch List',
        colorClass:'anomaly-warning',
        verdicts:  ['Category Slowdown', 'Overpriced vs Peers', 'Aged vs Peers', 'Above AI Estimate'],
    },
    {
        key:       'positive',
        label:     'Outperformers',
        colorClass:'anomaly-positive',
        verdicts:  ['Outperformer', 'High Interest'],
    },
];

// Units per verdict over `units`, counting only non-normal verdicts.
export const countVerdicts = (units) => {
    const counts = {};
    (units || []).forEach((u) => {
        if (u.AnomalyVerdict && u.AnomalySeverity !== 'normal') {
            counts[u.AnomalyVerdict] = (counts[u.AnomalyVerdict] || 0) + 1;
        }
    });
    return counts;
};

// Units per group key, from countVerdicts().
export const countGroups = (verdictCounts) => Object.fromEntries(
    ANOMALY_GROUPS.map((g) => [g.key, g.verdicts.reduce((s, v) => s + (verdictCounts[v] || 0), 0)]),
);

// Every verdict name the page knows, for parsing ?signal=.
export const ALL_VERDICTS = ANOMALY_GROUPS.flatMap((g) => g.verdicts);
